/**
 * PersonalboqenPublic.jsx – Öffentliche Mitarbeiter-Eingabe
 * Kein Login erforderlich · Mobile-optimiert · Signature Pad
 * Features: Adress-Autocomplete (Photon/OSM) + Searchable Krankenkassen-Dropdown
 */
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import SignatureCanvas from 'react-signature-canvas';
import SignaturePad from '../components/ui/SignaturePad';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  ChevronLeft, ChevronRight, CheckCircle2, Loader2,
  User, CreditCard, Landmark, RefreshCw,
  Search, X, AlertCircle, Building2, Shield, Lock,
} from 'lucide-react';
import {
  IBANInput, AddressAutocomplete, KrankenkassenSelect, validateIBAN,
  SVNummerInput, SteuerIDInput, CountrySelect, KVTypSelect, GermanDateInput,
} from '../components/PersonalboqenSmartFields';
import { DocumentVerificationStep } from '../components/DocumentVerificationStep';

const API = process.env.REACT_APP_BACKEND_URL;

// Sicheres Datum-Format: "2026-05-04" → "04.05.2026" (kein new Date() → kein iOS Safari Bug)
function fmtDateDE(isoStr) {
  if (!isoStr) return '';
  const p = isoStr.split('-');
  if (p.length !== 3) return isoStr;
  return `${p[2]}.${p[1]}.${p[0]}`;
}

// STEPS werden dynamisch basierend auf is_minijob berechnet (unten im Component)
const BASE_STEPS = [
  { id: 1, title: 'Persönliche Angaben', icon: User, color: 'from-blue-600 to-blue-700' },
  { id: 2, title: 'Steuer & SV', icon: CreditCard, color: 'from-violet-600 to-violet-700' },
  { id: 3, title: 'Bankdaten', icon: Landmark, color: 'from-teal-600 to-teal-700' },
  { id: 4, title: 'Unterschrift', icon: CheckCircle2, color: 'from-green-600 to-green-700' },
];

const RV_STEP = { id: 4, title: 'Rentenversicherung', icon: Shield, color: 'from-orange-600 to-orange-700' };
const WS_STEP = { id: 4, title: 'Studium & Nachweis', icon: Building2, color: 'from-indigo-600 to-indigo-700' };
const SIG_STEP_NO_RV = { id: 4, title: 'Unterschrift', icon: CheckCircle2, color: 'from-green-600 to-green-700' };
const SIG_STEP_WITH_RV = { id: 5, title: 'Unterschrift', icon: CheckCircle2, color: 'from-green-600 to-green-700' };

const emptyForm = () => ({
  // Personal
  familienname: '', vorname: '', geburtsname: '',
  strasse: '', hausnummer: '', plz: '', ort: '',
  telefon: '', email: '',
  geschlecht: '',
  familienstand: '',
  geburtsdatum: '', geburtsort: '',
  geburtsland: 'Deutschland',
  staatsangehoerigkeit: 'Deutschland',
  sv_nummer: '', krankenkasse: '',
  sv_erstantrag: false,
  kv_typ: '',
  schwerbehindert: false,
  // Aufenthaltsstatus (nur für Nicht-Deutsche)
  aufenthaltstitel_typ: '',
  aufenthalt_befristet: null,
  aufenthalt_bis: '',
  // Steuer (nur was Mitarbeiter weiß)
  steuer_id: '', steuerklasse: '', konfession: '',
  kinderfreibetraege: '0', finanzamt_nr: '',
  // Bank
  iban: '', bic: '', bank_name: '',
  iban_formatted: '', iban_valid: null,
});

function FieldGroup({ label, children }) {
  return (
    <div className="space-y-3">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{label}</h3>
      {children}
    </div>
  );
}

function Row({ children, cols = 2 }) {
  return (
    <div className={`grid gap-3 ${cols === 2 ? 'grid-cols-2' : cols === 3 ? 'grid-cols-3' : 'grid-cols-1'}`}>
      {children}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}{required && <span className="text-destructive ml-0.5">*</span>}</Label>
      {children}
    </div>
  );
}

function YesNo({ label, value, onChange }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      <div className="flex gap-2">
        {[['Ja', true], ['Nein', false]].map(([txt, val]) => (
          <button
            key={txt}
            type="button"
            onClick={() => onChange(val)}
            className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-all ${
              value === val
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border text-muted-foreground hover:border-muted-foreground/50'
            }`}
          >
            {txt}
          </button>
        ))}
      </div>
    </div>
  );
}

function DocCheck({ label, checked, onChange }) {
  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
        checked ? 'border-green-400 bg-green-50 dark:bg-green-900/20' : 'border-border'
      }`}
      onClick={() => onChange(!checked)}
    >
      <div className={`h-5 w-5 rounded border-2 flex items-center justify-center shrink-0 ${
        checked ? 'border-green-500 bg-green-500' : 'border-muted-foreground'
      }`}>
        {checked && <CheckCircle2 className="h-3.5 w-3.5 text-white" />}
      </div>
      <span className="text-sm">{label} – liegt vor</span>
    </div>
  );
}

export default function PersonalboqenPublic() {
  const { token } = useParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [bogenInfo, setBogenInfo] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [savingProgress, setSavingProgress] = useState(false);
  const sigRef = useRef(null);
  const [erklaerungBestaetigt, setErklaerungBestaetigt] = useState(false);
  const [dsgvoEinwilligung, setDsgvoEinwilligung] = useState(false);
  const [sigDatum, setSigDatum] = useState(new Date().toISOString().slice(0, 10));
  // RV-Entscheidung state (Minijob) – bewusste Wahl, NICHT vorausgewählt
  const [rvWahl, setRvWahl] = useState(null);             // null | 'befreiung' | 'pflicht'
  const [rvMerkblattGelesen, setRvMerkblattGelesen] = useState(false);
  const [rvPflichtBestaetigt, setRvPflichtBestaetigt] = useState(false);  // neutrale Kenntnisnahme beim Beibehalten
  const [rvMerkblattGelesenAt, setRvMerkblattGelesenAt] = useState(null); // Zeitpunkt Kenntnisnahme
  const [rvOrt, setRvOrt] = useState('');
  const [rvDatum, setRvDatum] = useState(new Date().toISOString().slice(0, 10));
  const rvSigRef = useRef(null);
  const [rvSignature, setRvSignature] = useState(null);  // erfasst beim Zeichnen (überlebt Step-Wechsel/Unmount)
  // PIN-Gate state
  const [pinVerified, setPinVerified] = useState(false);
  const [pbSession, setPbSession] = useState(null);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinChecking, setPinChecking] = useState(false);
  // KI-Verifikation state
  const [verificationEnabled, setVerificationEnabled] = useState(false);
  const [verifyStep, setVerifyStep] = useState('idle'); // idle | uploading | verifying | done | skipped
  const [verifyResults, setVerifyResults] = useState(null);
  const [verifyError, setVerifyError] = useState(null);
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [ibanCardImage, setIbanCardImage] = useState(null);
  const [fieldOverrides, setFieldOverrides] = useState({}); // {feldName: true} = "falsch erkannt"
  const [ibanVerifyResult, setIbanVerifyResult] = useState(null);
  const [dsgvoVerifyConsent, setDsgvoVerifyConsent] = useState(false);
  const [verifyDokumentTyp, setVerifyDokumentTyp] = useState(null);
  const [verifyAufenthaltstitel, setVerifyAufenthaltstitel] = useState(null);

  useEffect(() => {
    const fetchBogen = async () => {
      try {
        const res = await axios.get(`${API}/api/personalbogen/public/${token}`);
        setBogenInfo(res.data);
        setVerificationEnabled(!!res.data.enable_document_verification);
        if (res.data.personal?.email) {
          setForm(f => ({ ...f, email: res.data.personal.email || '' }));
        }
      } catch (e) {
        setError(e.response?.data?.detail || 'Link ungültig oder abgelaufen');
      } finally { setLoading(false); }
    };
    fetchBogen();
  }, [token]);

  const handlePinVerify = async () => {
    if (!pinInput.trim()) { setPinError('Bitte PIN eingeben'); return; }
    setPinChecking(true);
    setPinError('');
    try {
      const res = await axios.post(`${API}/api/personalbogen/public/${token}/verify-pin`, { pin: pinInput.trim() });
      const sess = res.data?.session || null;
      setPbSession(sess);
      // Sensible Daten (personal/bank/steuer) erst JETZT mit gültiger PIN-Session nachladen
      if (sess) {
        try {
          const full = await axios.get(`${API}/api/personalbogen/public/${token}`, { headers: { 'X-PB-Session': sess } });
          setBogenInfo(full.data);
          const p = full.data.personal || {};
          const b = full.data.bank || {};
          const s = full.data.steuer || {};
          const pick = (obj) => Object.fromEntries(
            Object.entries(obj || {}).filter(([, v]) => v !== null && v !== undefined && v !== '')
          );
          // Vollständige Wiederherstellung eines gespeicherten Zwischenstands (Fortsetzen)
          setForm(f => ({
            ...f,
            ...pick(p), ...pick(s), ...pick(b),
            iban_formatted: b.iban || f.iban_formatted,
          }));
        } catch (_) { /* nicht blockierend */ }
      }
      setPinVerified(true);
    } catch (e) {
      setPinError(e.response?.data?.detail || 'Falscher PIN');
    } finally { setPinChecking(false); }
  };

  // PIN-Session-Header für alle sensiblen Aufrufe nach erfolgreicher PIN-Prüfung.
  const sessCfg = () => (pbSession ? { headers: { 'X-PB-Session': pbSession } } : {});

  // Dynamische Steps
  const VERIFY_STEP = { id: 99, title: 'Dokument-Prüfung', icon: Shield, color: 'from-blue-600 to-blue-700' };
  const baseSteps = verificationEnabled
    ? [...BASE_STEPS.slice(0, 3), VERIFY_STEP, { ...BASE_STEPS[3], id: 5 }]
    : BASE_STEPS;

  const rvOnly = !!bogenInfo?.rv_decision_only;
  const STEPS = rvOnly
    ? [{ id: 1, title: 'Rentenversicherung', icon: Shield, color: 'from-orange-600 to-orange-700' }]
    : bogenInfo?.is_minijob
    ? [...baseSteps.slice(0, 3), ...(verificationEnabled ? [VERIFY_STEP] : []), RV_STEP, { id: verificationEnabled ? 6 : 5, title: 'Unterschrift', icon: CheckCircle2, color: 'from-green-600 to-green-700' }]
    : bogenInfo?.is_werkstudent
      ? [...baseSteps.slice(0, 3), ...(verificationEnabled ? [VERIFY_STEP] : []), WS_STEP, { id: verificationEnabled ? 6 : 5, title: 'Unterschrift', icon: CheckCircle2, color: 'from-green-600 to-green-700' }]
      : verificationEnabled
        ? [...BASE_STEPS.slice(0, 3), VERIFY_STEP, { id: 5, title: 'Unterschrift', icon: CheckCircle2, color: 'from-green-600 to-green-700' }]
        : [...BASE_STEPS.slice(0, 3), SIG_STEP_NO_RV];

  const verifyStepId = verificationEnabled ? 4 : null;
  const sigStepId  = bogenInfo?.is_minijob ? (verificationEnabled ? 6 : 5) : bogenInfo?.is_werkstudent ? (verificationEnabled ? 6 : 5) : (verificationEnabled ? 5 : 4);
  const rvStepId   = rvOnly ? 1 : (bogenInfo?.is_minijob ? (verificationEnabled ? 5 : 4) : null);
  const wsStepId   = bogenInfo?.is_werkstudent ? (verificationEnabled ? 5 : 4) : null;

  // Werkstudent Upload-State
  const [wsFile, setWsFile] = useState(null);
  const [wsUploading, setWsUploading] = useState(false);
  const [wsUploadDone, setWsUploadDone] = useState(false);
  const wsFileRef = useRef(null);

  const f = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));
  const fb = (field) => (val) => setForm(prev => ({ ...prev, [field]: val }));

  const validateStep = () => {
    if (step === 1) {
      if (!form.familienname || !form.vorname || !form.strasse || !form.ort || !form.plz)
        return 'Bitte fülle alle Pflichtfelder aus (Name, Adresse)';
      if (!form.email) return 'E-Mail-Adresse ist Pflicht';
      if (!form.geschlecht) return 'Bitte Geschlecht auswählen';
      if (!form.geburtsdatum) return 'Geburtsdatum ist Pflicht';
      if (!form.geburtsort) return 'Geburtsort ist Pflicht';
      if (!form.geburtsland) return 'Geburtsland ist Pflicht';
      if (!form.staatsangehoerigkeit) return 'Staatsangehörigkeit ist Pflicht';
      if (!form.sv_erstantrag && !form.sv_nummer) return 'SV-Nummer ist Pflicht – oder "Erstantrag notwendig" ankreuzen';
      if (!form.krankenkasse) return 'Krankenkasse ist Pflicht';
      if (!form.kv_typ) return 'Krankenversicherungstyp ist Pflicht';
      // Aufenthaltsstatus für Nicht-Deutsche
      if (form.staatsangehoerigkeit && form.staatsangehoerigkeit !== 'Deutschland') {
        if (!form.aufenthaltstitel_typ) return 'Bitte Aufenthaltsstatus angeben';
        if (!['eu_freizeit', 'niederlassungserlaubnis'].includes(form.aufenthaltstitel_typ)) {
          if (form.aufenthalt_befristet === null || form.aufenthalt_befristet === undefined) return 'Bitte befristet oder unbefristet angeben';
          if (form.aufenthalt_befristet === true && !form.aufenthalt_bis) return 'Bitte Ablaufdatum des Aufenthaltstitels angeben';
        }
      }
    }
    if (step === 2) {
      if (!form.steuer_id) return 'Steueridentifikationsnummer ist Pflicht';
      if (!form.steuerklasse) return 'Steuerklasse ist Pflicht';
      if (!form.konfession || form.konfession === 'sonstige') return 'Konfession ist Pflicht – bitte ausfüllen';
      if (form.kinderfreibetraege === '' || form.kinderfreibetraege === null) return 'Kinderfreibeträge ist Pflicht';
    }
    if (step === 3) {
      if (!form.iban) return 'IBAN ist Pflicht';
      const clean = form.iban.replace(/\s+/g, '');
      if (clean.length > 10 && !validateIBAN(clean)) return 'Ungültige IBAN – bitte prüfen';
    }
    if (step === rvStepId && bogenInfo?.is_minijob) {
      if (!rvMerkblattGelesen) return 'Bitte bestätige, dass du das Merkblatt gelesen hast';
      if (rvWahl === null) return 'Bitte entscheide dich: Befreiung beantragen oder versicherungspflichtig bleiben';
      if (rvWahl === 'befreiung') {
        if (!rvOrt.trim()) return 'Bitte trage deinen Ort ein';
        const padEmpty = !rvSigRef.current || rvSigRef.current.isEmpty();
        if (padEmpty && !rvSignature)
          return 'Bitte unterschreibe den Befreiungsantrag';
        if (!padEmpty) setRvSignature(rvSigRef.current.toDataURL('image/png'));
      }
      if (rvWahl === 'pflicht' && !rvPflichtBestaetigt) {
        return 'Bitte bestätige, dass du versicherungspflichtig bleiben möchtest';
      }
    }
    if (step === wsStepId && bogenInfo?.is_werkstudent) {
      if (!form.hochschule?.trim()) return 'Hochschule / Universität ist Pflicht';
      if (!form.matrikelnummer?.trim()) return 'Matrikelnummer ist Pflicht';
      if (!wsUploadDone) return 'Bitte lade deinen aktuellen Immatrikulationsnachweis hoch';
    }
    return null;
  };

  const handleWsUpload = async (file) => {
    if (!file) return;
    setWsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/api/personalbogen/public/${token}/upload-immatrikulation`, formData, sessCfg());
      setWsUploadDone(true);
      setWsFile(file);
      toast.success('Immatrikulationsnachweis hochgeladen ✅');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally { setWsUploading(false); }
  };

  const handleNext = () => {    const err = validateStep();
    if (err) { toast.error(err); return; }
    setStep(s => s + 1);
    window.scrollTo(0, 0);
  };

  // Zwischenspeichern: Entwurf sichern, später über denselben Link/PIN fortsetzen.
  const handleSaveProgress = async () => {
    setSavingProgress(true);
    try {
      await axios.post(`${API}/api/personalbogen/public/${token}/save-progress`, {
        personal: {
          familienname: form.familienname, vorname: form.vorname, geburtsname: form.geburtsname,
          strasse: form.strasse, hausnummer: form.hausnummer, plz: form.plz, ort: form.ort,
          telefon: form.telefon, email: form.email, geschlecht: form.geschlecht,
          familienstand: form.familienstand, geburtsdatum: form.geburtsdatum, geburtsort: form.geburtsort,
          geburtsland: form.geburtsland, staatsangehoerigkeit: form.staatsangehoerigkeit,
          sv_nummer: form.sv_nummer, krankenkasse: form.krankenkasse, kv_typ: form.kv_typ,
          sv_erstantrag: form.sv_erstantrag, schwerbehindert: form.schwerbehindert,
          aufenthaltstitel_typ: form.aufenthaltstitel_typ, aufenthalt_befristet: form.aufenthalt_befristet,
          aufenthalt_bis: form.aufenthalt_bis, schulabschluss: form.schulabschluss,
          berufsausbildung: form.berufsausbildung, hochschule: form.hochschule,
          matrikelnummer: form.matrikelnummer, studiengang: form.studiengang,
        },
        steuer: {
          steuer_id: form.steuer_id, steuerklasse: form.steuerklasse,
          konfession: form.konfession, kinderfreibetraege: form.kinderfreibetraege,
          finanzamt_nr: form.finanzamt_nr,
        },
        bank: { iban: form.iban, bic: form.bic, bank_name: form.bank_name },
      }, sessCfg());
      toast.success('Zwischenstand gespeichert – du kannst später über denselben Link & PIN fortsetzen.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally { setSavingProgress(false); }
  };

  const handleSubmit = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast.error('Bitte unterschreibe im Unterschriftsfeld');
      return;
    }
    if (!erklaerungBestaetigt) {
      toast.error('Bitte bestätige die Erklärung des Arbeitnehmers');
      return;
    }
    if (!dsgvoEinwilligung) {
      toast.error('Bitte bestätige die DSGVO-Datenschutzerklärung');
      return;
    }
    const sigDataUrl = sigRef.current.toDataURL('image/png');

    // RV-Entscheidung (Minijob): bewusste Wahl – Schweigen gilt NICHT als Befreiung
    let rv_antrag = null;
    if (bogenInfo?.is_minijob) {
      if (rvWahl === null) {
        toast.error('Bitte zur Rentenversicherung entscheiden, bevor du absendest.');
        return;
      }
      if (rvWahl === 'befreiung') {
        const rvSig = rvSignature
          || (rvSigRef.current && !rvSigRef.current.isEmpty()
                ? rvSigRef.current.toDataURL('image/png')
                : null);
        if (!rvSig) {
          toast.error('Der Befreiungsantrag muss unterschrieben werden, bevor du absendest.');
          return;
        }
        rv_antrag = {
          merkblatt_gelesen: rvMerkblattGelesen,
          ort_an: rvOrt,
          datum_unterschrift_an: fmtDateDE(rvDatum),
          signature_an: rvSig,
        };
      }
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/api/personalbogen/public/${token}`, {
        personal: {
          familienname: form.familienname, vorname: form.vorname,
          geburtsname: form.geburtsname, strasse: form.strasse,
          hausnummer: form.hausnummer, ort: form.ort, plz: form.plz,
          telefon: form.telefon, email: form.email,
          geschlecht: form.geschlecht, geburtsdatum: form.geburtsdatum,
          familienstand: form.familienstand,
          geburtsort: form.geburtsort,
          geburtsland: form.geburtsland,
          staatsangehoerigkeit: form.staatsangehoerigkeit,
          sv_nummer: form.sv_nummer, krankenkasse: form.krankenkasse,
          kv_typ: form.kv_typ,
          sv_erstantrag: form.sv_erstantrag,
          schwerbehindert: form.schwerbehindert,
          schulabschluss: form.schulabschluss,
          berufsausbildung: form.berufsausbildung,
          sv_ausweis_foto: undefined,
          kk_karte_foto: undefined,
          // Aufenthaltsstatus (Nicht-Deutsche)
          ...(form.staatsangehoerigkeit !== 'Deutschland' && {
            aufenthaltstitel_typ: form.aufenthaltstitel_typ || undefined,
            aufenthalt_befristet: form.aufenthalt_befristet !== null ? form.aufenthalt_befristet : undefined,
            aufenthalt_bis: form.aufenthalt_bis || undefined,
          }),
          // Werkstudent-Felder
          ...(bogenInfo?.is_werkstudent && {
            hochschule: form.hochschule,
            matrikelnummer: form.matrikelnummer,
            studiengang: form.studiengang,
          }),
        },
        steuer: {
          steuer_id: form.steuer_id, steuerklasse: form.steuerklasse,
          konfession: form.konfession, kinderfreibetraege: form.kinderfreibetraege,
          finanzamt_nr: form.finanzamt_nr,
        },
        bank: { iban: form.iban, bic: form.bic, bank_name: form.bank_name },
        beschaeftigung: {},
        dokumente: {},
        signature_arbeitnehmer: sigDataUrl,
        datum_unterschrift_arbeitnehmer: fmtDateDE(sigDatum),
        erklaerung_bestaetigt: erklaerungBestaetigt,
        dsgvo_einwilligung: dsgvoEinwilligung,
        rv_antrag: rv_antrag,
        rv_wahl: bogenInfo?.is_minijob ? rvWahl : undefined,
        rv_merkblatt_version: bogenInfo?.is_minijob ? (bogenInfo?.rv_merkblatt_version || undefined) : undefined,
        rv_merkblatt_gelesen_at: bogenInfo?.is_minijob ? (rvMerkblattGelesenAt || undefined) : undefined,
      }, sessCfg());
      setSubmitted(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Einreichen');
    } finally { setSubmitting(false); }
  };

  // Wechsel-Fall: Mitarbeiter trifft NUR die RV-Entscheidung (Merkblatt + bewusste Wahl).
  const handleRvDecisionSubmit = async () => {
    if (!rvMerkblattGelesen) { toast.error('Bitte bestätige, dass du das Merkblatt gelesen hast'); return; }
    if (rvWahl === null) { toast.error('Bitte entscheide dich: Befreiung beantragen oder versicherungspflichtig bleiben'); return; }
    let rv_antrag = null;
    if (rvWahl === 'befreiung') {
      if (!rvOrt.trim()) { toast.error('Bitte trage deinen Ort ein'); return; }
      const rvSig = rvSignature
        || (rvSigRef.current && !rvSigRef.current.isEmpty() ? rvSigRef.current.toDataURL('image/png') : null);
      if (!rvSig) { toast.error('Bitte unterschreibe den Befreiungsantrag'); return; }
      rv_antrag = {
        merkblatt_gelesen: rvMerkblattGelesen,
        ort_an: rvOrt,
        datum_unterschrift_an: fmtDateDE(rvDatum),
        signature_an: rvSig,
      };
    }
    if (rvWahl === 'pflicht' && !rvPflichtBestaetigt) {
      toast.error('Bitte bestätige, dass du versicherungspflichtig bleiben möchtest'); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/personalbogen/public/${token}/rv-decision`, {
        rv_wahl: rvWahl,
        rv_merkblatt_version: bogenInfo?.rv_merkblatt_version || undefined,
        rv_merkblatt_gelesen_at: rvMerkblattGelesenAt || new Date().toISOString(),
        rv_antrag,
      }, sessCfg());
      setSubmitted(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern der Entscheidung');
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="max-w-sm w-full">
        <CardContent className="py-12 text-center space-y-3">
          <p className="text-2xl">⚠️</p>
          <p className="font-semibold text-foreground">Link ungültig</p>
          <p className="text-sm text-muted-foreground">{error}</p>
        </CardContent>
      </Card>
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="max-w-sm w-full">
        <CardContent className="py-12 text-center space-y-4">
          <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto" />
          <div>
            <h2 className="text-xl font-bold">Vielen Dank!</h2>
            <p className="text-muted-foreground mt-2 text-sm">
              Dein Personalbogen wurde erfolgreich eingereicht. Dein Arbeitgeber prüft die Daten und legt deinen Account an.
            </p>
          </div>
          <div className="bg-muted/50 rounded-lg p-3 text-xs text-muted-foreground">
            Du kannst dieses Fenster jetzt schließen.
          </div>
        </CardContent>
      </Card>
    </div>
  );

  if (!pinVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-sm">
          <div className="bg-card rounded-2xl shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white text-center">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <Lock className="h-8 w-8" />
              </div>
              <h1 className="text-xl font-bold">Sicherer Zugang</h1>
              <p className="text-sm opacity-80 mt-1">{bogenInfo?.firma || 'Personalfragebogen'}</p>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-muted-foreground text-center">
                Bitte gib deinen persönlichen 6-stelligen PIN-Code ein, um fortzufahren.
                Du hast diesen von deinem Arbeitgeber erhalten.
              </p>
              <div className="space-y-2">
                <label className="text-sm font-medium">PIN-Code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={pinInput}
                  onChange={e => { setPinInput(e.target.value.replace(/\D/g, '')); setPinError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handlePinVerify()}
                  placeholder="000000"
                  className={`w-full h-12 rounded-xl border text-center text-2xl font-bold tracking-[0.5em]
                    focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors
                    ${pinError ? 'border-red-500 bg-red-50 dark:bg-red-900/10' : 'border-border bg-background'}`}
                  data-testid="pin-input"
                  autoFocus
                />
                {pinError && (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5" /> {pinError}
                  </p>
                )}
              </div>
              <Button onClick={handlePinVerify} disabled={pinChecking || pinInput.length < 1}
                className="w-full h-12 text-base" data-testid="pin-submit-btn">
                {pinChecking ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Zugang öffnen →'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentStep = STEPS[step - 1];

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <div className={`bg-gradient-to-r ${currentStep.color} text-white px-4 pt-10 pb-6`}>
        <p className="text-xs font-medium opacity-75 mb-1">{bogenInfo?.firma || 'Personalbogen'}</p>
        <h1 className="text-xl font-bold">Personalfragebogen</h1>
        <p className="text-sm opacity-80 mt-0.5">Schritt {step} von {STEPS.length}: {currentStep.title}</p>
        {/* Progress bar */}
        <div className="mt-4 h-1.5 bg-white/30 rounded-full overflow-hidden">
          <div className="h-full bg-white rounded-full transition-all duration-500" style={{ width: `${(step / STEPS.length) * 100}%` }} />
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-lg mx-auto p-4 space-y-6 mt-4">

        {/* Arbeitgeber-Rückfrage: konkrete Nachbesserung am selben Vorgang */}
        {bogenInfo?.info_request?.message && (
          <div className="bg-amber-50 dark:bg-amber-950/40 border-2 border-amber-300 dark:border-amber-700 rounded-xl p-4" data-testid="info-request-banner">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-bold text-amber-800 dark:text-amber-200">Rückfrage deines Arbeitgebers</p>
                <p className="text-sm text-amber-900 dark:text-amber-100 mt-1 whitespace-pre-wrap" data-testid="info-request-message">{bogenInfo.info_request.message}</p>
                {Array.isArray(bogenInfo.info_request.fields) && bogenInfo.info_request.fields.length > 0 && (
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-2">Betrifft: {bogenInfo.info_request.fields.join(', ')}</p>
                )}
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-2">Bitte ergänze/korrigiere die betroffenen Angaben und reiche den Bogen erneut ein.</p>
              </div>
            </div>
          </div>
        )}

        {/* Job-Konditionen Karte (Admin-Daten, nur lesend) */}
        {(bogenInfo?.vergutung || bogenInfo?.stunden_pro_woche || bogenInfo?.berufsbezeichnung) && (
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/40 dark:to-indigo-950/40 border border-blue-200 dark:border-blue-800 rounded-xl p-4" data-testid="job-kondition-card">
            <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider mb-3">
              Deine Stelle bei {bogenInfo.firma || 'deinem Arbeitgeber'}
            </p>
            <div className="flex flex-wrap gap-4">
              {bogenInfo.berufsbezeichnung && (
                <div>
                  <p className="text-xs text-muted-foreground">Position</p>
                  <p className="text-sm font-semibold text-foreground">{bogenInfo.berufsbezeichnung}</p>
                </div>
              )}
              {bogenInfo.stunden_pro_woche && (
                <div>
                  <p className="text-xs text-muted-foreground">Stunden / Woche</p>
                  <p className="text-sm font-semibold text-foreground">{bogenInfo.stunden_pro_woche} Std.</p>
                </div>
              )}
              {bogenInfo.vergutung && (
                <div>
                  <p className="text-xs text-muted-foreground">Vergütung</p>
                  <p className="text-sm font-bold text-blue-700 dark:text-blue-300">{bogenInfo.vergutung}</p>
                </div>
              )}
              {bogenInfo.arbeitszeit && (
                <div>
                  <p className="text-xs text-muted-foreground">Beschäftigung</p>
                  <p className="text-sm font-semibold text-foreground">
                    {bogenInfo.arbeitszeit === 'minijob' ? 'Aushilfe (Minijob)' : bogenInfo.arbeitszeit === 'vollzeit' ? 'Vollzeit' : bogenInfo.arbeitszeit === 'teilzeit' ? 'Teilzeit' : bogenInfo.arbeitszeit === 'werkstudent' ? 'Werkstudent' : bogenInfo.arbeitszeit}
                  </p>
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-3">Diese Angaben wurden von deinem Arbeitgeber eingetragen und sind nicht änderbar.</p>
          </div>
        )}

        {/* Step 1: Persönliche Angaben */}
        {!rvOnly && step === 1 && (
          <div className="space-y-5">
            <FieldGroup label="Name">
              <Row><Field label="Familienname" required><Input value={form.familienname} onChange={f('familienname')} data-testid="familienname-input" /></Field>
              <Field label="Vorname" required><Input value={form.vorname} onChange={f('vorname')} data-testid="vorname-input" /></Field></Row>
              <Field label="Geburtsname (falls abweichend)"><Input value={form.geburtsname} onChange={f('geburtsname')} /></Field>
            </FieldGroup>
            <FieldGroup label="Adresse">
              <AddressAutocomplete
                strasse={form.strasse}
                hausnummer={form.hausnummer}
                plz={form.plz}
                ort={form.ort}
                onChange={(updates) => setForm(prev => ({ ...prev, ...updates }))}
              />
            </FieldGroup>
            <FieldGroup label="Kontakt">
              <Row>
                <Field label="Telefon (Mobil)"><Input value={form.telefon} onChange={f('telefon')} inputMode="tel" placeholder="z.B. 0170 123456" /></Field>
                <Field label="E-Mail" required><Input value={form.email} onChange={f('email')} inputMode="email" type="email" placeholder="name@beispiel.de" /></Field>
              </Row>
            </FieldGroup>
            <FieldGroup label="Geburt & Person">
              <div className="space-y-1.5">
                <Label className="text-sm">Geschlecht <span className="text-destructive">*</span></Label>
                <div className="flex gap-2">
                  {['männlich', 'weiblich', 'diverse'].map(g => (
                    <button key={g} type="button" onClick={() => setForm(prev => ({ ...prev, geschlecht: g }))}
                      className={`flex-1 py-2 text-xs rounded-lg border font-medium transition-all ${form.geschlecht === g ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'}`}>
                      {g}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Familienstand</Label>
                <div className="grid grid-cols-3 gap-2">
                  {['ledig', 'verheiratet', 'geschieden', 'verwitwet', 'getrennt lebend', 'Lebenspartnerschaft'].map(fs => (
                    <button key={fs} type="button"
                      onClick={() => setForm(prev => ({ ...prev, familienstand: prev.familienstand === fs ? '' : fs }))}
                      className={`py-2 text-xs rounded-lg border font-medium transition-all ${form.familienstand === fs ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:border-muted-foreground/50'}`}
                      data-testid={`familienstand-${fs.replace(' ', '-')}`}>
                      {fs}
                    </button>
                  ))}
                </div>
              </div>
              <Row>
                <Field label="Geburtsdatum" required><GermanDateInput value={form.geburtsdatum} onChange={(iso) => setForm(p => ({...p, geburtsdatum: iso}))} /></Field>
                <Field label="Geburtsort" required><Input value={form.geburtsort} onChange={f('geburtsort')} placeholder="z.B. Stuttgart" /></Field>
              </Row>
              <Row>
                <Field label="Geburtsland" required>
                  <CountrySelect
                    value={form.geburtsland}
                    onChange={val => setForm(p => ({ ...p, geburtsland: val }))}
                    testId="geburtsland-select"
                    placeholder="Land auswählen…"
                  />
                </Field>
                <Field label="Staatsangehörigkeit" required>
                  <CountrySelect
                    value={form.staatsangehoerigkeit}
                    onChange={val => setForm(p => ({ ...p, staatsangehoerigkeit: val }))}
                    testId="staatsangehoerigkeit-select"
                    placeholder="Staatsangehörigkeit…"
                  />
                </Field>
              </Row>
            </FieldGroup>
            {/* ── Aufenthaltsstatus – nur für Nicht-Deutsche ── */}
            {form.staatsangehoerigkeit && form.staatsangehoerigkeit !== 'Deutschland' && (
              <FieldGroup label="Aufenthaltsstatus in Deutschland">
                <Field label="Art des Aufenthalts" required>
                  <select
                    value={form.aufenthaltstitel_typ || ''}
                    onChange={e => {
                      const val = e.target.value;
                      setForm(p => ({
                        ...p,
                        aufenthaltstitel_typ: val,
                        aufenthalt_befristet: val === 'niederlassungserlaubnis' || val === 'eu_freizeit' ? false : p.aufenthalt_befristet,
                        aufenthalt_bis: val === 'niederlassungserlaubnis' || val === 'eu_freizeit' ? '' : p.aufenthalt_bis,
                      }));
                    }}
                    className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                    data-testid="aufenthaltstitel-typ-select"
                  >
                    <option value="">Bitte auswählen…</option>
                    <option value="eu_freizeit">EU-Bürger / Freizügigkeit (kein Aufenthaltstitel nötig)</option>
                    <option value="niederlassungserlaubnis">Niederlassungserlaubnis (unbefristet)</option>
                    <option value="aufenthaltstitel">Aufenthaltstitel (§ AufenthG)</option>
                    <option value="blaue_karte_eu">Blaue Karte EU</option>
                    <option value="fiktionsbescheinigung">Fiktionsbescheinigung</option>
                    <option value="sonstige">Sonstiger Aufenthaltsstatus</option>
                  </select>
                </Field>

                {/* Befristet/Unbefristet */}
                {form.aufenthaltstitel_typ && !['eu_freizeit', 'niederlassungserlaubnis'].includes(form.aufenthaltstitel_typ) && (
                  <>
                    <div className="space-y-1.5">
                      <Label className="text-sm">Gültigkeit <span className="text-destructive">*</span></Label>
                      <div className="flex gap-2">
                        <button type="button"
                          onClick={() => setForm(p => ({ ...p, aufenthalt_befristet: false, aufenthalt_bis: '' }))}
                          className={`flex-1 py-2.5 text-sm rounded-lg border font-medium transition-all ${form.aufenthalt_befristet === false ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300' : 'border-border text-muted-foreground hover:border-muted-foreground/50'}`}
                          data-testid="aufenthalt-unbefristet-btn">
                          Unbefristet
                        </button>
                        <button type="button"
                          onClick={() => setForm(p => ({ ...p, aufenthalt_befristet: true }))}
                          className={`flex-1 py-2.5 text-sm rounded-lg border font-medium transition-all ${form.aufenthalt_befristet === true ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300' : 'border-border text-muted-foreground hover:border-muted-foreground/50'}`}
                          data-testid="aufenthalt-befristet-btn">
                          Befristet
                        </button>
                      </div>
                    </div>
                    {form.aufenthalt_befristet === true && (
                      <Field label="Gültig bis (Ablaufdatum)" required>
                        <Input
                          type="date"
                          value={form.aufenthalt_bis || ''}
                          onChange={f('aufenthalt_bis')}
                          data-testid="aufenthalt-bis-input"
                        />
                      </Field>
                    )}
                    {form.aufenthalt_befristet === false && (
                      <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-700 rounded-lg">
                        <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <p className="text-xs text-green-700 dark:text-green-300">Unbefristeter Aufenthalt – kein Ablaufdatum erforderlich</p>
                      </div>
                    )}
                    {/* Warnung wenn Ablaufdatum bald */}
                    {form.aufenthalt_befristet === true && form.aufenthalt_bis && (() => {
                      const days = Math.ceil((new Date(form.aufenthalt_bis) - new Date()) / 86400000);
                      if (days <= 0) return (
                        <div className="p-3 bg-red-50 dark:bg-red-900/10 border border-red-300 dark:border-red-700 rounded-lg">
                          <p className="text-xs text-red-700 dark:text-red-300 font-semibold">Aufenthaltstitel ist abgelaufen! Bitte sofort verlängern.</p>
                        </div>
                      );
                      if (days <= 90) return (
                        <div className="p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700 rounded-lg">
                          <p className="text-xs text-amber-700 dark:text-amber-300 font-medium">Aufenthaltstitel läuft in {days} Tagen ab – bitte rechtzeitig verlängern.</p>
                        </div>
                      );
                      return null;
                    })()}
                  </>
                )}
                {form.aufenthaltstitel_typ === 'eu_freizeit' && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-700 rounded-lg">
                    <p className="text-xs text-blue-700 dark:text-blue-300">Als EU-Bürger haben Sie das Recht auf Freizügigkeit. Ein Aufenthaltstitel ist nicht erforderlich.</p>
                  </div>
                )}
                {form.aufenthaltstitel_typ === 'niederlassungserlaubnis' && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-700 rounded-lg">
                    <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                    <p className="text-xs text-green-700 dark:text-green-300">Niederlassungserlaubnis – dauerhaft unbefristet, kein Ablaufdatum.</p>
                  </div>
                )}
              </FieldGroup>
            )}
            <FieldGroup label="Sozialversicherung">
              <SVNummerInput
                value={form.sv_nummer}
                onChange={(val) => setForm(prev => ({ ...prev, sv_nummer: val }))}
                required={!form.sv_erstantrag}
              />
              {/* SV-Erstantrag Checkbox */}
              <div
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${form.sv_erstantrag ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20' : 'border-border'}`}
                onClick={() => setForm(p => ({ ...p, sv_erstantrag: !p.sv_erstantrag, sv_nummer: !p.sv_erstantrag ? '' : p.sv_nummer }))}
                data-testid="sv-erstantrag-checkbox"
              >
                <div className={`h-5 w-5 rounded border-2 flex items-center justify-center shrink-0 ${form.sv_erstantrag ? 'border-amber-500 bg-amber-500' : 'border-muted-foreground'}`}>
                  {form.sv_erstantrag && <CheckCircle2 className="h-3.5 w-3.5 text-white" />}
                </div>
                <div>
                  <p className="text-sm font-medium">SV-Nummer noch nicht bekannt</p>
                  <p className="text-xs text-muted-foreground">Erstantrag notwendig – wird vom Arbeitgeber beantragt</p>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-sm">Krankenkasse <span className="text-destructive">*</span></Label>
                <KrankenkassenSelect
                  value={form.krankenkasse}
                  onChange={(val) => setForm(prev => ({ ...prev, krankenkasse: val }))}
                />
              </div>
              <KVTypSelect
                value={form.kv_typ}
                onChange={val => setForm(p => ({ ...p, kv_typ: val }))}
                required
              />
              <YesNo label="Schwerbehindert?" value={form.schwerbehindert} onChange={fb('schwerbehindert')} />
            </FieldGroup>
            <FieldGroup label="Ausbildung & Qualifikation">
              <Field label="Höchster Schulabschluss">
                <select value={form.schulabschluss} onChange={f('schulabschluss')}
                  className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                  data-testid="schulabschluss-select">
                  <option value="">Auswählen (optional)…</option>
                  <option value="ohne_abschluss">Ohne Schulabschluss</option>
                  <option value="hauptschule">Haupt-/Volksschulabschluss</option>
                  <option value="mittlere_reife">Mittlere Reife / gleichwertiger Abschluss</option>
                  <option value="abitur">Abitur / Fachabitur</option>
                  <option value="in_ausbildung">Noch in schulischer Ausbildung</option>
                </select>
              </Field>
              <Field label="Höchste Berufsausbildung">
                <select value={form.berufsausbildung} onChange={f('berufsausbildung')}
                  className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                  data-testid="berufsausbildung-select">
                  <option value="">Auswählen (optional)…</option>
                  <option value="ohne_ausbildung">Ohne beruflichen Ausbildungsabschluss</option>
                  <option value="berufsausbildung">Anerkannte Berufsausbildung</option>
                  <option value="meister">Meister / Techniker / gleichwertiger Fachschulabschluss</option>
                  <option value="bachelor">Bachelor / Diplom / Magister / Master / Staatsexamen</option>
                  <option value="promotion">Promotion</option>
                  <option value="in_ausbildung">Noch in beruflicher Ausbildung</option>
                </select>
              </Field>
            </FieldGroup>
          </div>
        )}

        {/* Step 2: Steuer & SV (NUR was der Mitarbeiter kennt) */}
        {step === 2 && (
          <div className="space-y-5">
            <FieldGroup label="Steuer">
              <Field label="Steueridentifikationsnummer" required>
              <SteuerIDInput
                value={form.steuer_id}
                onChange={(val) => setForm(prev => ({ ...prev, steuer_id: val }))}
                required
              />
              </Field>
              <Row>
                <Field label="Steuerklasse" required>
                  <select value={form.steuerklasse} onChange={f('steuerklasse')}
                    className="h-9 w-full rounded-md border bg-background px-3 text-sm">
                    <option value="">Wählen...</option>
                    {['I','II','III','IV','V','VI'].map(k => <option key={k} value={k}>Klasse {k}</option>)}
                  </select>
                </Field>
                <Field label="Konfession" required>
                  {(() => {
                    const KNOWN = ['', 'ev', 'rk', 'lt', 'kein'];
                    const dropVal = KNOWN.includes(form.konfession) ? form.konfession : 'sonstige';
                    return (
                      <div className="space-y-2">
                        <select value={dropVal}
                          onChange={e => setForm(p => ({ ...p, konfession: e.target.value === 'sonstige' ? 'sonstige' : e.target.value }))}
                          className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                          data-testid="konfession-select">
                          <option value="">Auswählen…</option>
                          <option value="ev">Evangelisch (ev)</option>
                          <option value="rk">Römisch-Katholisch (rk)</option>
                          <option value="lt">Lutherisch (lt)</option>
                          <option value="kein">Keine Konfession (kein)</option>
                          <option value="sonstige">Sonstige – bitte angeben</option>
                        </select>
                        {dropVal === 'sonstige' && (
                          <Input
                            value={form.konfession === 'sonstige' ? '' : form.konfession}
                            onChange={e => setForm(p => ({ ...p, konfession: e.target.value || 'sonstige' }))}
                            placeholder="z.B. fr, jd, islamisch …"
                            className="h-9"
                            autoFocus
                            data-testid="konfession-custom-input"
                          />
                        )}
                      </div>
                    );
                  })()}
                </Field>
              </Row>
              <Row>
                <Field label="Kinderfreibeträge" required>
                  <Input value={form.kinderfreibetraege} onChange={f('kinderfreibetraege')} type="number" min="0" step="0.5" />
                </Field>
                <Field label="Finanzamt-Nummer">
                  <Input value={form.finanzamt_nr} onChange={f('finanzamt_nr')} />
                </Field>
              </Row>
            </FieldGroup>
            <div className="bg-muted/40 rounded-lg p-3 text-xs text-muted-foreground">
              Die vertraglichen Details (Befristung, Arbeitszeit, Probezeit) werden von deinem Arbeitgeber eingetragen.
            </div>
          </div>
        )}

        {/* Step 3: Bankdaten mit IBAN-Intelligenz */}
        {step === 3 && (
          <div className="space-y-5">
            <FieldGroup label="Bankverbindung">
              <IBANInput
                value={form.iban_formatted || form.iban}
                onChange={(formatted, clean) => setForm(prev => ({
                  ...prev, iban: clean, iban_formatted: formatted, iban_valid: null
                }))}
                onBICFound={(bic, bankName) => {
                  setForm(prev => ({ ...prev, bic, bank_name: bankName }));
                }}
              />
              <Row>
                <div className="space-y-1.5">
                  <Label className="text-sm flex items-center gap-1.5">
                    <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                    BIC
                    {form.bic && <span className="text-xs text-green-600 font-normal">(auto ermittelt)</span>}
                  </Label>
                  <Input value={form.bic} onChange={f('bic')} placeholder="z.B. COBADEFFXXX" className="font-mono text-sm" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm flex items-center gap-1.5">
                    Kreditinstitut
                    {form.bank_name && <span className="text-xs text-green-600 font-normal">(auto ermittelt)</span>}
                  </Label>
                  <Input value={form.bank_name} onChange={f('bank_name')} placeholder="Bank" className="text-sm" />
                </div>
              </Row>
            </FieldGroup>
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-xs text-blue-700 dark:text-blue-300">
              Deine Bankdaten werden ausschließlich für die Lohnüberweisung verwendet und sicher gespeichert.
            </div>
          </div>
        )}

        {/* ─── KI-DOKUMENT-VERIFIKATION (Step 4 wenn aktiviert) ─── */}
        {step === verifyStepId && verificationEnabled && (
          <DocumentVerificationStep
            token={token}
            sessionHeaders={pbSession ? { 'X-PB-Session': pbSession } : {}}
            form={form}
            frontImage={frontImage}
            setFrontImage={setFrontImage}
            backImage={backImage}
            setBackImage={setBackImage}
            ibanCardImage={ibanCardImage}
            setIbanCardImage={setIbanCardImage}
            verifyStep={verifyStep}
            setVerifyStep={setVerifyStep}
            verifyResults={verifyResults}
            setVerifyResults={setVerifyResults}
            verifyError={verifyError}
            setVerifyError={setVerifyError}
            fieldOverrides={fieldOverrides}
            setFieldOverrides={setFieldOverrides}
            ibanVerifyResult={ibanVerifyResult}
            setIbanVerifyResult={setIbanVerifyResult}
            dsgvoConsent={dsgvoVerifyConsent}
            setDsgvoConsent={setDsgvoVerifyConsent}
            setForm={setForm}
            verifyDokumentTyp={verifyDokumentTyp}
            setVerifyDokumentTyp={setVerifyDokumentTyp}
            verifyAufenthaltstitel={verifyAufenthaltstitel}
            setVerifyAufenthaltstitel={setVerifyAufenthaltstitel}
          />
        )}

        {/* Step 4/5: RV-Befreiungsantrag (nur Minijob) / Werkstudent-Studium */}
        {step === wsStepId && bogenInfo?.is_werkstudent && (
          <div className="space-y-5">
            <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-2xl p-4">
              <p className="font-semibold text-indigo-900 dark:text-indigo-100 text-sm mb-1">Werkstudenten-Status</p>
              <p className="text-xs text-indigo-700 dark:text-indigo-300">
                Du wirst als Werkstudent beschäftigt – max. <strong>{bogenInfo.max_stunden_semester || 20} Std./Woche</strong> im Semester,
                bis zu <strong>{bogenInfo.max_stunden_semesterferien || 40} Std./Woche</strong> in den Semesterferien.
                Bitte fülle deine Studiendaten aus und lade deinen aktuellen Immatrikulationsnachweis hoch.
              </p>
            </div>

            <FieldGroup label="Studiendaten">
              <Field label="Hochschule / Universität" required>
                <Input value={form.hochschule} onChange={f('hochschule')}
                  placeholder="z.B. Hochschule Esslingen" data-testid="hochschule-input" />
              </Field>
              <Row>
                <Field label="Matrikelnummer" required>
                  <Input value={form.matrikelnummer} onChange={f('matrikelnummer')}
                    placeholder="z.B. 123456" data-testid="matrikelnummer-input" />
                </Field>
                <Field label="Studiengang">
                  <Input value={form.studiengang} onChange={f('studiengang')}
                    placeholder="z.B. Wirtschaftsinformatik" />
                </Field>
              </Row>
            </FieldGroup>

            <FieldGroup label="Immatrikulationsnachweis (Pflicht)">
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Lade die aktuelle Immatrikulationsbescheinigung deiner Hochschule hoch.
                  Akzeptiert: PDF, JPG, PNG (max. 10 MB). Muss jedes Semester aktualisiert werden.
                </p>
                {wsUploadDone ? (
                  <div className="flex items-center gap-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-3">
                    <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-green-800 dark:text-green-300">Nachweis hochgeladen ✅</p>
                      <p className="text-xs text-green-700 dark:text-green-400">{wsFile?.name}</p>
                    </div>
                    <button onClick={() => { setWsUploadDone(false); setWsFile(null); }}
                      className="ml-auto text-xs text-green-600 underline hover:text-green-800">Ersetzen</button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => wsFileRef.current?.click()}
                    disabled={wsUploading}
                    className="w-full border-2 border-dashed border-indigo-300 rounded-xl py-8 flex flex-col items-center gap-2 hover:bg-indigo-50/30 dark:hover:bg-indigo-950/20 transition-colors disabled:opacity-50"
                    data-testid="ws-upload-btn"
                  >
                    {wsUploading ? (
                      <><Loader2 className="h-6 w-6 text-indigo-400 animate-spin" /><span className="text-sm text-indigo-500">Wird hochgeladen...</span></>
                    ) : (
                      <><Building2 className="h-6 w-6 text-indigo-300" /><span className="text-sm font-medium text-indigo-600">Immatrikulationsnachweis hochladen</span><span className="text-xs text-muted-foreground">PDF, JPG oder PNG · max. 10 MB</span></>
                    )}
                  </button>
                )}
                <input ref={wsFileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic"
                  className="hidden" onChange={e => e.target.files?.[0] && handleWsUpload(e.target.files[0])} />
              </div>
            </FieldGroup>
          </div>
        )}

        {/* ─── Step 4 (Minijob): RV-Entscheidung (Befreiung ODER versicherungspflichtig bleiben) ─────────── */}
        {step === rvStepId && bogenInfo?.is_minijob && (
          <div className="space-y-5">
            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-xl p-4 space-y-2">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-orange-600 shrink-0" />
                <p className="font-semibold text-sm text-orange-800 dark:text-orange-300">Rentenversicherung – deine Entscheidung</p>
              </div>
              <p className="text-xs text-orange-700 dark:text-orange-400">
                Ein Minijob ist grundsätzlich rentenversicherungspflichtig (dein Eigenanteil liegt bei 3,6 % des Verdienstes,
                z.&nbsp;B. ca. 21,71&nbsp;€ bei 603&nbsp;€ im Monat). Du kannst dich auf Antrag davon <strong>befreien</strong> lassen
                oder <strong>versicherungspflichtig bleiben</strong>. Bitte lies zuerst das offizielle Merkblatt und triff dann eine Entscheidung.
              </p>
            </div>

            {/* Offizielles Merkblatt – lesbar & herunterladbar VOR der Entscheidung */}
            <a
              href={`${API}/api/personalbogen/public/${token}/rv-merkblatt-pdf`}
              target="_blank" rel="noopener noreferrer"
              data-testid="rv-merkblatt-download"
              className="flex items-center gap-2 p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors text-sm font-medium"
            >
              <Shield className="h-4 w-4 text-orange-600 shrink-0" />
              Offizielles Merkblatt zur Rentenversicherung öffnen / herunterladen (PDF)
            </a>

            {/* Merkblatt-Kenntnisnahme (getrennt protokolliert) */}
            <div
              className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${rvMerkblattGelesen ? 'border-orange-400 bg-orange-50 dark:bg-orange-900/20' : 'border-border'}`}
              onClick={() => setRvMerkblattGelesen(v => { const nv = !v; setRvMerkblattGelesenAt(nv ? new Date().toISOString() : null); return nv; })}
              data-testid="rv-merkblatt-checkbox"
            >
              <div className={`h-5 w-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 ${rvMerkblattGelesen ? 'border-orange-500 bg-orange-500' : 'border-muted-foreground'}`}>
                {rvMerkblattGelesen && <CheckCircle2 className="h-3.5 w-3.5 text-white" />}
              </div>
              <div>
                <p className="font-semibold text-sm">Ich habe das Merkblatt zur Rentenversicherung gelesen. *</p>
                <p className="text-xs text-muted-foreground mt-0.5">Die Kenntnisnahme wird mit Version und Zeitpunkt protokolliert.</p>
              </div>
            </div>

            {/* Bewusste Wahl – NICHT vorausgewählt */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Wie möchtest du dich entscheiden? *</Label>
              <div className="grid gap-3">
                <button type="button" onClick={() => setRvWahl('befreiung')} data-testid="rv-wahl-befreiung"
                  className={`text-left p-4 rounded-xl border-2 transition-all ${rvWahl === 'befreiung' ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20' : 'border-border hover:border-orange-300'}`}>
                  <p className="font-semibold text-sm">Befreiung beantragen</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Kein Eigenanteil; du erwirbst in diesem Minijob keine zusätzlichen Rentenansprüche. Antrag mit Unterschrift erforderlich (§ 6 Abs. 1b SGB VI).</p>
                </button>
                <button type="button" onClick={() => setRvWahl('pflicht')} data-testid="rv-wahl-pflicht"
                  className={`text-left p-4 rounded-xl border-2 transition-all ${rvWahl === 'pflicht' ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' : 'border-border hover:border-emerald-300'}`}>
                  <p className="font-semibold text-sm">Versicherungspflichtig bleiben</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Kleiner Eigenanteil (3,6 %); dafür volle Rentenansprüche. Kein Antrag nötig.</p>
                </button>
              </div>
            </div>

            {/* Weg A: Befreiung → Antrag + Unterschrift */}
            {rvWahl === 'befreiung' && (
              <div className="space-y-5 border-t pt-4" data-testid="rv-befreiung-block">
                <div className="space-y-2 bg-muted/40 rounded-lg p-3">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Deine Daten (vorausgefüllt)</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-muted-foreground">Name:</span> <strong>{form.familienname}</strong></div>
                    <div><span className="text-muted-foreground">Vorname:</span> <strong>{form.vorname}</strong></div>
                    <div className="col-span-2"><span className="text-muted-foreground">RV-Nummer:</span> <strong className="font-mono">{form.sv_nummer || '–'}</strong></div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Mit deiner Unterschrift beantragst du die Befreiung von der Rentenversicherungspflicht für diese geringfügig entlohnte Beschäftigung (§ 6 Abs. 1b SGB VI).
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-sm">Ort *</Label>
                    <Input value={rvOrt} onChange={e => setRvOrt(e.target.value)} placeholder="z.B. Fellbach" data-testid="rv-ort-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm">Datum</Label>
                    <input type="date" value={rvDatum} onChange={e => setRvDatum(e.target.value)}
                      className="w-full h-10 px-3 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Unterschrift Antragsteller <span className="text-destructive">*</span></Label>
                  <div className="border-2 border-border rounded-xl overflow-hidden bg-white dark:bg-gray-900">
                    <SignaturePad ref={rvSigRef} penColor="#EA580C" height={160}
                      onEnd={() => setRvSignature(rvSigRef.current && !rvSigRef.current.isEmpty() ? rvSigRef.current.toDataURL('image/png') : null)} />
                  </div>
                  <button type="button" onClick={() => { rvSigRef.current?.clear(); setRvSignature(null); }}
                    className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
                    <RefreshCw className="h-3.5 w-3.5" /> Zurücksetzen
                  </button>
                </div>
              </div>
            )}

            {/* Weg B: Versicherungspflichtig bleiben → neutrales Bestätigungsfenster (Eigenanteil UND Vorteile) */}
            {rvWahl === 'pflicht' && (
              <div className="space-y-4 border-t pt-4" data-testid="rv-pflicht-block">
                <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4 space-y-3">
                  <p className="font-semibold text-sm text-emerald-800 dark:text-emerald-300">Du bleibst rentenversicherungspflichtig – das bedeutet:</p>
                  <div className="grid sm:grid-cols-2 gap-3 text-xs">
                    <div className="space-y-1">
                      <p className="font-semibold text-foreground">Dein Eigenanteil</p>
                      <p className="text-muted-foreground">3,6 % deines Verdienstes (z.&nbsp;B. ca. 21,71&nbsp;€ bei 603&nbsp;€ im Monat) werden vom Lohn einbehalten.</p>
                    </div>
                    <div className="space-y-1">
                      <p className="font-semibold text-foreground">Deine Vorteile</p>
                      <p className="text-muted-foreground">Volle Pflichtbeitragszeiten für Rente und Reha, höhere spätere Rente und Anrechnung z.&nbsp;B. bei der Grundrente.</p>
                    </div>
                  </div>
                  <p className="text-[11px] text-muted-foreground">Hinweis: Dies ist keine Nettolohn-Garantie. Deine Entscheidung ist freiwillig; du kannst dich auch befreien lassen.</p>
                </div>
                <div
                  className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${rvPflichtBestaetigt ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20' : 'border-border'}`}
                  onClick={() => setRvPflichtBestaetigt(v => !v)}
                  data-testid="rv-pflicht-checkbox"
                >
                  <div className={`h-5 w-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 ${rvPflichtBestaetigt ? 'border-emerald-500 bg-emerald-500' : 'border-muted-foreground'}`}>
                    {rvPflichtBestaetigt && <CheckCircle2 className="h-3.5 w-3.5 text-white" />}
                  </div>
                  <div>
                    <p className="font-semibold text-sm">Ich möchte versicherungspflichtig bleiben. *</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Mir ist bewusst, dass ein Eigenanteil anfällt und ich dafür volle Rentenansprüche erwerbe. Ein Befreiungsantrag ist nicht erforderlich.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Unterschrift-Step */}
        {step === sigStepId && (
          <div className="space-y-5">
            {/* Zusammenfassung der Angaben – vor der Unterschrift prüfen */}
            <div className="bg-card border border-border rounded-xl p-4 space-y-3" data-testid="angaben-zusammenfassung">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-foreground">Bitte prüfe deine Angaben</p>
                <button type="button" onClick={() => { setStep(1); window.scrollTo(0, 0); }}
                  className="text-xs text-primary underline" data-testid="summary-edit-btn">Ändern</button>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                <div><span className="text-muted-foreground">Name:</span> <strong>{form.vorname} {form.familienname}</strong></div>
                <div><span className="text-muted-foreground">Geburtsdatum:</span> <strong>{fmtDateDE(form.geburtsdatum) || '–'}</strong></div>
                <div className="col-span-2"><span className="text-muted-foreground">Adresse:</span> <strong>{[`${form.strasse} ${form.hausnummer}`.trim(), `${form.plz} ${form.ort}`.trim()].filter(Boolean).join(', ') || '–'}</strong></div>
                <div className="col-span-2"><span className="text-muted-foreground">E-Mail:</span> <strong>{form.email || '–'}</strong></div>
                <div><span className="text-muted-foreground">SV-Nr.:</span> <strong>{form.sv_erstantrag ? 'Erstantrag' : (form.sv_nummer || '–')}</strong></div>
                <div><span className="text-muted-foreground">Krankenkasse:</span> <strong>{form.krankenkasse || '–'}</strong></div>
                <div><span className="text-muted-foreground">Steuer-ID:</span> <strong>{form.steuer_id || '–'}</strong></div>
                <div><span className="text-muted-foreground">Steuerklasse:</span> <strong>{form.steuerklasse || '–'}</strong></div>
                <div className="col-span-2"><span className="text-muted-foreground">IBAN:</span> <strong className="font-mono">{form.iban || '–'}</strong></div>
                {bogenInfo?.is_minijob && (
                  <div className="col-span-2"><span className="text-muted-foreground">Rentenversicherung:</span> <strong>{rvWahl === 'befreiung' ? 'Befreiung beantragt' : rvWahl === 'pflicht' ? 'Versicherungspflichtig bleiben' : 'noch offen'}</strong></div>
                )}
              </div>
            </div>
            {/* DSGVO Datenschutzhinweis */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 space-y-2">
              <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">Datenschutzhinweis (DSGVO)</p>
              <p className="text-xs text-blue-700 dark:text-blue-400 leading-relaxed">
                Deine personenbezogenen Daten (Name, Adresse, Steuer- und Sozialversicherungsdaten) werden ausschließlich
                zur Begründung, Durchführung und Beendigung des Beschäftigungsverhältnisses sowie zur Lohnabrechnung
                verarbeitet (Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO, § 26 BDSG).
                Die Daten werden nicht an Dritte weitergegeben, außer an Behörden (Finanzamt, Rentenversicherung,
                Krankenkasse) soweit gesetzlich vorgeschrieben. Du hast jederzeit das Recht auf Auskunft,
                Berichtigung und Löschung deiner Daten.
              </p>
            </div>
            {/* DSGVO Pflicht-Checkbox */}
            <div
              className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${dsgvoEinwilligung ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20' : 'border-border bg-background'}`}
              onClick={() => setDsgvoEinwilligung(v => !v)}
              data-testid="dsgvo-checkbox"
            >
              <div className={`h-5 w-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all ${dsgvoEinwilligung ? 'border-blue-500 bg-blue-500' : 'border-muted-foreground'}`}>
                {dsgvoEinwilligung && <CheckCircle2 className="h-3.5 w-3.5 text-white" />}
              </div>
              <div>
                <p className="font-semibold text-sm text-foreground">Datenschutz-Einwilligung *</p>
                <p className="text-sm text-foreground mt-1">Ich bestätige, dass meine Daten zum Zwecke der Abrechnung und Vertragsgestaltung digital verarbeitet werden.</p>
                <p className="text-xs text-muted-foreground mt-0.5">Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO, § 26 BDSG.</p>
              </div>
            </div>
            <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground">
              <p className="font-semibold text-foreground mb-2">Erklärung des Arbeitnehmers</p>
              <p>Ich versichere, dass die vorstehenden Angaben vollständig und richtig sind. Ich verpflichte mich, alle zukünftigen Änderungen meiner persönlichen Verhältnisse unverzüglich mitzuteilen. Ich bin damit einverstanden, dass die Daten für die Lohnabrechnung verarbeitet werden (DSGVO Art. 6 Abs. 1 lit. b).</p>
            </div>

            {/* Pflicht-Checkbox Erklärung */}
            <div
              className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${erklaerungBestaetigt ? 'border-primary bg-primary/10' : 'border-border bg-background'}`}
              onClick={() => setErklaerungBestaetigt(v => !v)}
              data-testid="erklaerung-checkbox"
            >
              <div className={`h-5 w-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all ${erklaerungBestaetigt ? 'border-primary bg-primary' : 'border-muted-foreground'}`}>
                {erklaerungBestaetigt && <CheckCircle2 className="h-3.5 w-3.5 text-white" />}
              </div>
              <div>
                <p className="font-semibold text-sm text-foreground">Ich versichere die Richtigkeit meiner Angaben *</p>
                <p className="text-xs text-muted-foreground mt-0.5">Ich verpflichte mich, alle Änderungen (insbesondere weitere Beschäftigung nach Art, Dauer und Entgelt) unverzüglich mitzuteilen.</p>
              </div>
            </div>

            {/* Unterschriftsdatum */}
            <div className="space-y-1.5">
              <Label className="text-sm">Datum der Unterschrift</Label>
              <input
                type="date"
                value={sigDatum}
                onChange={e => setSigDatum(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="sig-datum-input"
              />
              <p className="text-xs text-muted-foreground">Vorausgefüllt mit dem heutigen Datum – bei Bedarf ändern.</p>
            </div>
            <div className="space-y-2">
              <Label>Unterschrift *</Label>
              <p className="text-xs text-muted-foreground">Bitte mit dem Finger (oder Maus) unterschreiben:</p>
              <div className="border-2 border-border rounded-xl overflow-hidden bg-white dark:bg-gray-900">
                <SignaturePad ref={sigRef} penColor="#1E40AF" height={200} />
              </div>
              <button
                type="button"
                onClick={() => sigRef.current?.clear()}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
              >
                <RefreshCw className="h-3.5 w-3.5" /> Unterschrift zurücksetzen
              </button>
            </div>
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-xs text-amber-700 dark:text-amber-300">
              Mit deiner Unterschrift bestätigst du die Richtigkeit aller eingegebenen Daten.
            </div>
          </div>
        )}

        {/* Zwischenspeichern (Daten-Steps) */}
        {!rvOnly && step >= 1 && step <= 3 && (
          <div className="flex justify-center -mb-2">
            <button type="button" onClick={handleSaveProgress} disabled={savingProgress}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground underline disabled:opacity-50"
              data-testid="save-progress-btn">
              {savingProgress ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Speichern & später fortsetzen
            </button>
          </div>
        )}

        {/* Navigation */}
        <div className="flex gap-3 pt-2">
          {step > 1 && (
            <Button variant="outline" className="flex-1" onClick={() => { setStep(s => s - 1); window.scrollTo(0, 0); }}>
              <ChevronLeft className="h-4 w-4 mr-1" /> Zurück
            </Button>
          )}
        {step < STEPS.length ? (
            <Button className="flex-1" onClick={handleNext} data-testid="next-step-btn">
              Weiter <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : rvOnly ? (
            <Button className="flex-1 bg-orange-600 hover:bg-orange-700" onClick={handleRvDecisionSubmit} disabled={submitting} data-testid="submit-rv-decision-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              RV-Entscheidung speichern
            </Button>
          ) : (
            <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={handleSubmit} disabled={submitting} data-testid="submit-bogen-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Personalbogen einreichen
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
